import React, { useMemo, useState } from 'react'
import { Alert, Box, Button, Grid, Stack, TextField, Typography } from '@mui/material'
import MerchantToolShell, { shellStyles } from './MerchantToolShell'
import { useLanguage } from '../../contexts/LanguageContext'
import { createPosRequest, getPosRequests } from '../../utils/merchantToolStorage'

const keypadValues = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'backspace']

export default function MerchantPosMode() {
    const { lang } = useLanguage()
    const isFr = lang === 'fr'
    const [amount, setAmount] = useState('')
    const [note, setNote] = useState('')
    const [presentedRequest, setPresentedRequest] = useState(null)
    const [error, setError] = useState('')

    const parsedAmount = useMemo(() => Number(amount || 0), [amount])
    const recentRequests = useMemo(() => getPosRequests().slice(0, 3), [])

    const handleKeypadPress = (value) => {
        setError('')
        if (value === 'backspace') {
            setAmount((current) => current.slice(0, -1))
            return
        }

        setAmount((current) => {
            if (value === '.' && current.includes('.')) return current
            if (value === '.' && current.length === 0) return '0.'
            return `${current}${value}`
        })
    }

    const handlePresentPayment = () => {
        if (!(parsedAmount > 0)) {
            setError(isFr ? 'Ajoutez un montant local valide.' : 'Add a valid local amount.')
            return
        }

        const request = createPosRequest({
            amount: parsedAmount,
            note: note.trim(),
        })

        setPresentedRequest(request)
        setError('')
    }

    const handleCopyReference = async () => {
        if (!presentedRequest?.reference) return
        try {
            await navigator.clipboard.writeText(presentedRequest.reference)
        } catch {
            // no-op fallback for older browsers
        }
    }

    return (
        <MerchantToolShell
            eyebrow={isFr ? 'Paiement local' : 'Local acceptance'}
            title="POS Mode"
            subtitle={
                isFr
                    ? 'Saisissez le montant comme sur mobile, ajoutez une note de caisse si besoin, puis presentez le paiement au client.'
                    : 'Enter the amount like the mobile POS flow, add an optional checkout note, then present the payment to the customer.'
            }
            chips={[
                isFr ? 'Faible data' : 'Low data',
                isFr ? 'Clavier rapide' : 'Fast keypad',
                isFr ? 'Preuve prete' : 'Proof ready',
            ]}
            primaryAction={{
                label: isFr ? 'Afficher paiement' : 'Present Payment',
                onClick: handlePresentPayment,
            }}
            left={
                <Stack spacing={2.2}>
                    {error && <Alert severity="error">{error}</Alert>}
                    {presentedRequest && (
                        <Alert severity="success">
                            {isFr
                                ? `Paiement cree. Reference ${presentedRequest.reference}. Total ${presentedRequest.total.toFixed(2)}.`
                                : `Payment created. Reference ${presentedRequest.reference}. Total ${presentedRequest.total.toFixed(2)}.`}
                        </Alert>
                    )}

                    <Typography
                        sx={{
                            textAlign: 'center',
                            fontWeight: 900,
                            color: '#FF8A00',
                            fontSize: { xs: 34, md: 42 },
                        }}
                    >
                        {amount || '0'}
                    </Typography>

                    <TextField
                        label={isFr ? 'Description (facultative)' : 'Description (optional)'}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder={isFr ? 'Ex: boissons, table 4' : 'Ex: drinks, table 4'}
                    />

                    <Grid container spacing={1.6}>
                        {keypadValues.map((value) => {
                            const isDelete = value === 'backspace'
                            return (
                                <Grid item xs={4} key={value}>
                                    <Button
                                        fullWidth
                                        onClick={() => handleKeypadPress(value)}
                                        sx={{
                                            minHeight: 112,
                                            borderRadius: 4,
                                            textTransform: 'none',
                                            fontSize: 28,
                                            fontWeight: 500,
                                            color: '#FFFFFF',
                                            bgcolor: isDelete ? '#FF555A' : 'rgba(91,46,255,0.10)',
                                            border: isDelete
                                                ? '1px solid rgba(255,85,90,0.55)'
                                                : '1px solid rgba(183,166,255,0.16)',
                                            '&:hover': {
                                                bgcolor: isDelete ? '#FF6469' : 'rgba(91,46,255,0.18)',
                                            },
                                        }}
                                    >
                                        {isDelete ? '⌫' : value}
                                    </Button>
                                </Grid>
                            )
                        })}
                    </Grid>
                </Stack>
            }
            right={
                <Stack spacing={2}>
                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 0.8 }}>
                            {isFr ? 'Paiement presente' : 'Presented payment'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 1.2 }}>
                            {isFr
                                ? 'Le client suit ensuite le flux MojoPayr et la preuve revient dans les transactions.'
                                : 'The customer then follows the MojoPayr payment flow and the proof returns to transactions.'}
                        </Typography>
                        <Typography sx={{ fontWeight: 900, fontSize: 30 }}>
                            {Number.isFinite(parsedAmount) ? parsedAmount.toFixed(2) : '0.00'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', mt: 1, fontSize: 13 }}>
                            {note || (isFr ? 'Ajoutez une note facultative pour la caisse.' : 'Add an optional checkout note.')}
                        </Typography>
                    </Box>

                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 1.2 }}>
                            {isFr ? 'Paiements POS recents' : 'Recent POS payments'}
                        </Typography>
                        {presentedRequest ? (
                            <Stack spacing={0.8}>
                                <Typography sx={{ fontWeight: 800 }}>{presentedRequest.reference}</Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {isFr
                                        ? `Montant ${presentedRequest.amount.toFixed(2)} | Frais ${presentedRequest.fee.toFixed(2)} | Total ${presentedRequest.total.toFixed(2)}`
                                        : `Amount ${presentedRequest.amount.toFixed(2)} | Fee ${presentedRequest.fee.toFixed(2)} | Total ${presentedRequest.total.toFixed(2)}`}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={handleCopyReference}
                                    sx={{
                                        alignSelf: 'flex-start',
                                        color: '#FFFFFF',
                                        borderColor: 'rgba(183,166,255,0.34)',
                                        borderRadius: 4,
                                        textTransform: 'none',
                                        fontWeight: 800,
                                        mt: 0.5,
                                    }}
                                >
                                    {isFr ? 'Copier reference' : 'Copy Reference'}
                                </Button>
                            </Stack>
                        ) : recentRequests.length > 0 ? (
                            <Stack spacing={1}>
                                {recentRequests.map((request) => (
                                    <Box key={request.id} sx={{ ...shellStyles.softPanel, p: 1.5 }}>
                                        <Typography sx={{ fontWeight: 800 }}>{request.reference}</Typography>
                                        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                            {isFr
                                                ? `Montant ${Number(request.amount).toFixed(2)} | Total ${Number(request.total).toFixed(2)}`
                                                : `Amount ${Number(request.amount).toFixed(2)} | Total ${Number(request.total).toFixed(2)}`}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                {isFr
                                    ? 'Aucune presentation POS creee pour le moment.'
                                    : 'No POS payment request created yet.'}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            }
        />
    )
}
